import apiClient from '../lib/axios';
import { RECIPIENT_CARRIER_BUCKETS, type CarrierDisplay } from '../config/carriers';
import { REMINDER_STATUSES, type ReminderStatusKey } from '../config/reminderStatuses';

type CarrierKey = CarrierDisplay['key'];

/** One carrier's reminder jobs, counted by status. */
export type CarrierSmsCounts = Record<ReminderStatusKey, number>;

export interface DashboardStats {
  totalRecipients: number;
  upcomingVisits: number;
  /** Messages ever dispatched (sentAt set), not the transient 'sent' status. */
  totalRemindersSent: number;
  /** Queued, not yet dispatched. */
  pendingReminders: number;
  /** Outcome split of totalRemindersSent; the three always sum to it. */
  smsDelivered: number;
  smsFailedAfterDispatch: number;
  smsAwaitingConfirmation: number;
  lastSyncedAt: string | null;
  visitsWithSms: number;
  /**
   * Every recipient, bucketed by the carrier they can be reached on. 'unknown'
   * is the ones with no usable number, so the three sum to totalRecipients.
   */
  carrierBreakdown: Record<CarrierKey, number>;
  /** Reminder jobs per carrier, split by status. Counts jobs, not people. */
  smsByCarrier: Record<CarrierKey, CarrierSmsCounts>;
}

/** One day of the daily SMS chart. */
export interface DailyChartPoint {
  date: string;
  smsSent: number;
}

export interface VaccineReminderCounts {
  /** Ever dispatched, matching SMS Sent. */
  sent: number;
  /** Queued, matching Pending Reminders. */
  pending: number;
}

/** One dose's reminders. The dose list and labels come from the server. */
export interface DoseReminderCounts extends VaccineReminderCounts {
  dose: string;
  label: string;
}

export interface RemindersByVaccine {
  child: DoseReminderCounts[];
  td: DoseReminderCounts[];
  unknown: VaccineReminderCounts;
}

/**
 * What the dashboard shows before the first response lands, and when the stats
 * request fails. Built from the shared constants rather than written out: this
 * used to be two hand-typed copies, and adding one status meant editing six
 * nested objects by hand.
 */
export const EMPTY_DASHBOARD_STATS: DashboardStats = {
  totalRecipients: 0,
  upcomingVisits: 0,
  totalRemindersSent: 0,
  pendingReminders: 0,
  smsDelivered: 0,
  smsFailedAfterDispatch: 0,
  smsAwaitingConfirmation: 0,
  lastSyncedAt: null,
  visitsWithSms: 0,
  carrierBreakdown: Object.fromEntries(
    RECIPIENT_CARRIER_BUCKETS.map((c) => [c.key, 0])
  ) as Record<CarrierKey, number>,
  smsByCarrier: Object.fromEntries(
    RECIPIENT_CARRIER_BUCKETS.map((c) => [
      c.key,
      Object.fromEntries(REMINDER_STATUSES.map((s) => [s, 0])) as CarrierSmsCounts,
    ])
  ) as Record<CarrierKey, CarrierSmsCounts>,
};

class DashboardService {
  async getStats(): Promise<DashboardStats> {
    const response = await apiClient.get('/dashboard/stats');
    return response.data.data;
  }

  async getDailyChart(days: number = 30): Promise<DailyChartPoint[]> {
    const response = await apiClient.get('/dashboard/daily-chart', { params: { days } });
    return response.data.data;
  }

  async getRemindersByVaccine(): Promise<RemindersByVaccine> {
    const response = await apiClient.get('/dashboard/vaccines');
    return response.data.data;
  }

  async triggerSync() {
    const response = await apiClient.post('/sync/run');
    return response.data.data;
  }

  async getSyncStatus() {
    const response = await apiClient.get('/sync/status');
    return response.data.data;
  }

  async getScheduleStatus() {
    const response = await apiClient.get('/reminders/schedule/status');
    return response.data.data;
  }

  async scheduleReminders() {
    const response = await apiClient.post('/reminders/schedule');
    return response.data.data;
  }
}

export const dashboardService = new DashboardService();
